import { Quote, Star, ArrowUpRight } from "lucide-react";
import FadeIn, { SectionHeading } from "./FadeIn";
import TiltCard from "./motion/TiltCard";

// Genuine Google reviews for Akashdeep Ghai (ProMortgages / My First Home Mortgage Ltd)
// Source: https://g.page/r/Cad1hG8_6mlHEAE/review
const GOOGLE_REVIEWS_URL = "https://g.page/r/Cad1hG8_6mlHEAE/review";

const FEATURED_REVIEWS = [
  {
    name: "Betelihem Mengesha",
    date: "4 months ago",
    paragraphs: [
      "I had an excellent experience working with Akashdeep from ProMortgages. He has been instrumental in helping us purchase and sell multiple investment properties, and his expertise truly stands out.",
      "Akashdeep took the time to understand our goals and always provided honest, knowledgeable advice. Whether we were buying or selling, he guided us through every step with professionalism and patience. His understanding of the market, attention to detail, and strong negotiation skills helped us secure great deals and maximize our returns.",
      "What we appreciated most was his reliability and communication — he was always available to answer our questions and made the entire process smooth and stress-free. He genuinely cares about his clients and goes above and beyond to ensure success.",
      "We highly recommend Akashdeep and the ProMortgages team to anyone looking for a trustworthy and skilled mortgage professional, especially for investment properties.",
    ],
  },
  {
    name: "Njeunji Yami Axel Cedric",
    date: "4 months ago",
    paragraphs: [
      "Navigating the stressful process of securing a lender can be overwhelming, but working with Akashdeep made all the difference. From the very beginning, he was knowledgeable, responsive, and genuinely committed to helping me find the best solution.",
      "His professionalism and attention to detail were outstanding, and he took the time to clearly explain each step, which gave me a lot of confidence throughout the process. Everything moved smoothly and efficiently, and thanks to his dedication and expertise, I was successfully approved without unnecessary delays.",
      "What really stood out was his patience, transparency, and willingness to go the extra mile to ensure everything was handled properly. I highly recommend reaching out to Akashdeep if you're looking for someone reliable and trustworthy — you'll be in great hands. Thank you again for your excellent service!",
    ],
  },
];

const SHORT_REVIEWS = [
  {
    name: "Aniket Sharma",
    date: "6 months ago",
    text: "Very experienced and knowledgeable broker. Gets fast approvals and best rates available in industry.",
  },
  {
    name: "Jordan Mojaveli",
    date: "4 months ago",
    text: "The service was perfect and we are very happy for that.",
  },
  {
    name: "Taranjeet Seth",
    date: "6 months ago",
    text: null,
  },
];

const CARD_TRANSFORMS = [
  "lg:[transform:rotateY(6deg)_translateZ(-40px)_translateY(14px)]",
  "lg:[transform:translateZ(35px)]",
  "lg:[transform:rotateY(-6deg)_translateZ(-40px)_translateY(14px)]",
];

const Stars = ({ testId }) => (
  <div data-testid={testId} className="flex items-center gap-1" aria-label="5 out of 5 stars">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={13} strokeWidth={1} className="fill-gold text-gold" />
    ))}
  </div>
);

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.86c2.26-2.09 3.57-5.17 3.57-8.81z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.86-3c-1.07.72-2.44 1.15-4.08 1.15-3.14 0-5.8-2.12-6.75-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.25 14.28A7.2 7.2 0 0 1 4.87 12c0-.79.14-1.56.38-2.28V6.63H1.29a12 12 0 0 0 0 10.74l3.96-3.09z" />
    <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.97 11.97 0 0 0 12 0 11.99 11.99 0 0 0 1.29 6.63l3.96 3.09C6.2 6.89 8.86 4.77 12 4.77z" />
  </svg>
);

export default function Testimonials({ onLead }) {
  return (
    <section id="stories" data-testid="testimonials-section" className="py-24 lg:py-36 bg-charcoal/95">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeading
          label="Client Success Stories"
          title="Quiet Confidence, Loud Results"
          sub="Genuine five-star Google reviews from clients Akashdeep has guided home."
        />

        <FadeIn className="mt-12 flex justify-center">
          <a
            data-testid="google-reviews-badge"
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-4 rounded-sm border border-gold/30 bg-charcoal-700/60 backdrop-blur-xl px-7 py-4 transition-[border-color,box-shadow] duration-500 hover:border-gold/60 hover:shadow-[0_0_40px_rgba(229,192,123,0.12)]"
          >
            <GoogleMark />
            <span className="font-serif text-2xl text-ivory leading-none">5.0</span>
            <Stars testId="google-badge-stars" />
            <span className="hidden sm:block h-6 w-px bg-white/10" />
            <span className="hidden sm:block text-[10px] uppercase tracking-[0.22em] text-ivory/50 group-hover:text-gold/80 transition-colors duration-300">
              Verified Google Reviews
            </span>
            <ArrowUpRight size={14} strokeWidth={1.5} className="text-gold/60 group-hover:text-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-[transform,color] duration-300" />
          </a>
        </FadeIn>

        <div className="mt-16 grid lg:grid-cols-2 gap-6 lg:gap-8 [transform-style:preserve-3d] [perspective:1400px]">
          {FEATURED_REVIEWS.map((r, i) => (
            <FadeIn key={r.name} delay={i * 0.12} variant="depth">
              <TiltCard max={3} className="h-full [transform-style:preserve-3d]">
                <figure
                  data-testid={`testimonial-card-${i}`}
                  className="glass-panel rounded-md p-9 lg:p-11 h-full flex flex-col border-gold/25 transition-[border-color,box-shadow] duration-500 hover:border-gold/50"
                >
                  <div className="flex items-center justify-between gap-4 mb-7">
                    <Quote size={24} strokeWidth={1.25} className="text-gold" />
                    <Stars testId={`testimonial-stars-${i}`} />
                  </div>
                  <blockquote className="text-ivory/75 font-light leading-relaxed text-[15px] flex-1 space-y-4">
                    {r.paragraphs.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </blockquote>
                  <figcaption className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between gap-4">
                    <div>
                      <p className="font-serif text-lg text-ivory">{r.name}</p>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-gold/60 mt-1.5">Google Review · {r.date}</p>
                    </div>
                    <GoogleMark />
                  </figcaption>
                </figure>
              </TiltCard>
            </FadeIn>
          ))}
        </div>

        <div className="mt-6 lg:mt-8 grid md:grid-cols-3 gap-6 lg:gap-8 [transform-style:preserve-3d] [perspective:1400px]">
          {SHORT_REVIEWS.map((r, i) => (
            <FadeIn key={r.name} delay={i * 0.1} variant="depth">
              <TiltCard max={4} className="h-full [transform-style:preserve-3d]">
                <figure
                  data-testid={`testimonial-card-${FEATURED_REVIEWS.length + i}`}
                  className={`glass-panel rounded-md p-8 h-full flex flex-col transition-[border-color,box-shadow] duration-500 hover:border-gold/50 ${CARD_TRANSFORMS[i]}`}
                >
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <Quote size={18} strokeWidth={1.25} className="text-gold" />
                    <Stars testId={`testimonial-stars-${FEATURED_REVIEWS.length + i}`} />
                  </div>
                  <blockquote className="text-ivory/70 font-light leading-relaxed text-sm flex-1">
                    {r.text ? <p>{r.text}</p> : <p className="italic text-ivory/45">Left a five-star rating on Google.</p>}
                  </blockquote>
                  <figcaption className="mt-7 pt-5 border-t border-white/10 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-serif text-ivory">{r.name}</p>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-gold/60 mt-1">Google Review · {r.date}</p>
                    </div>
                    <GoogleMark />
                  </figcaption>
                </figure>
              </TiltCard>
            </FadeIn>
          ))}
        </div>

        <FadeIn className="mt-20 text-center">
          <p data-testid="testimonials-cta-line" className="font-serif text-2xl md:text-3xl text-ivory tracking-tight">
            Want a mortgage strategy <span className="gold-text-gradient italic">built around your goals?</span>
          </p>
          <button
            data-testid="testimonials-book-btn"
            onClick={() => onLead && onLead("consultation")}
            className="mt-8 px-10 py-4 text-xs tracking-[0.22em] font-semibold text-charcoal bg-gold hover:bg-champagne transition-colors duration-300 rounded-sm"
          >
            BOOK A CONSULTATION
          </button>
        </FadeIn>
      </div>
    </section>
  );
}
